(() => {
  const initialize = (root) => {
    if (!root || root.dataset.skyHeaderReady === 'true') return;
    root.dataset.skyHeaderReady = 'true';

    const searchPanel = root.querySelector('[data-sky-search-panel]');
    const mobileMenu = root.querySelector('[data-sky-mobile-menu]');
    const localePanel = root.querySelector('[data-sky-locale-panel]');
    const support = root.querySelector('.sky-header-support');
    const supportButton = root.querySelector('[data-sky-support-button]');
    let lastTrigger = null;

    const lockPage = () => document.body.classList.add('sky-header-lock');
    const unlockPage = () => {
      if (![searchPanel, mobileMenu, localePanel].some((panel) => panel && !panel.hidden)) document.body.classList.remove('sky-header-lock');
    };
    const openPanel = (panel, trigger, focusSelector) => {
      if (!panel) return;
      [searchPanel, mobileMenu, localePanel].forEach((item) => { if (item && item !== panel) item.hidden = true; });
      lastTrigger = trigger;
      panel.hidden = false;
      trigger?.setAttribute('aria-expanded', 'true');
      lockPage();
      requestAnimationFrame(() => panel.querySelector(focusSelector)?.focus());
    };
    const closePanel = (panel) => {
      if (!panel || panel.hidden) return;
      panel.hidden = true;
      root.querySelectorAll('[aria-expanded="true"]').forEach((item) => item.setAttribute('aria-expanded', 'false'));
      unlockPage();
      lastTrigger?.focus();
    };
    const closeAll = () => {
      [searchPanel, mobileMenu, localePanel].forEach(closePanel);
      support?.classList.remove('is-open');
      supportButton?.setAttribute('aria-expanded', 'false');
    };

    root.querySelectorAll('[data-sky-search-open]').forEach((button) => button.addEventListener('click', () => openPanel(searchPanel, button, '[data-sky-header-search-input]')));
    root.querySelectorAll('[data-sky-search-close]').forEach((button) => button.addEventListener('click', () => closePanel(searchPanel)));
    root.querySelectorAll('[data-sky-menu-open]').forEach((button) => button.addEventListener('click', () => openPanel(mobileMenu, button, 'input')));
    root.querySelectorAll('[data-sky-menu-close]').forEach((button) => button.addEventListener('click', () => closePanel(mobileMenu)));
    root.querySelectorAll('[data-sky-locale-open]').forEach((button) => button.addEventListener('click', () => openPanel(localePanel, button, 'select')));
    root.querySelectorAll('[data-sky-locale-close]').forEach((button) => button.addEventListener('click', () => closePanel(localePanel)));

    supportButton?.addEventListener('click', () => {
      const open = !support.classList.contains('is-open');
      support.classList.toggle('is-open', open);
      supportButton.setAttribute('aria-expanded', String(open));
    });
    document.addEventListener('click', (event) => {
      if (support && !support.contains(event.target)) {
        support.classList.remove('is-open');
        supportButton?.setAttribute('aria-expanded', 'false');
      }
    });
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') closeAll(); });

    const onScroll = () => root.classList.toggle('is-scrolled', window.scrollY > 8);
    onScroll(); window.addEventListener('scroll', onScroll, { passive: true });

    let cartRefreshController;
    const refreshCartDrawer = async () => {
      const drawer = document.getElementById('cart-drawer');
      const currentInner = drawer?.querySelector('.cart-drawer__inner');
      if (!drawer || !currentInner) return;

      cartRefreshController?.abort();
      cartRefreshController = new AbortController();

      try {
        const sectionUrl = new URL(window.location.href);
        sectionUrl.search = '';
        sectionUrl.searchParams.set('section_id', 'cart-drawer-section');

        const response = await fetch(sectionUrl, {
          headers: { Accept: 'text/html' },
          cache: 'no-store',
          credentials: 'same-origin',
          signal: cartRefreshController.signal,
        });
        if (!response.ok) throw new Error(`Cart drawer refresh failed: ${response.status}`);

        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextInner = documentFragment.querySelector('#cart-drawer .cart-drawer__inner, .cart-drawer__inner');
        if (!nextInner || !drawer.isConnected || !currentInner.isConnected) return;

        const sectionStyles = [...documentFragment.querySelectorAll('style')]
          .map((style) => style.textContent || '')
          .filter(Boolean)
          .join('\n');
        if (sectionStyles) {
          let styleElement = document.getElementById('SkyCartDrawerDynamicStyles');
          if (!styleElement) {
            styleElement = document.createElement('style');
            styleElement.id = 'SkyCartDrawerDynamicStyles';
            document.head.appendChild(styleElement);
          }
          styleElement.textContent = sectionStyles;
        }

        currentInner.replaceWith(document.importNode(nextInner, true));
      } catch (error) {
        if (error.name !== 'AbortError') console.warn('[SkySimo cart] Unable to refresh cart drawer.', error);
      }
    };

    document.addEventListener('shopify:cart:lines-update', (event) => {
      event.promise?.then(refreshCartDrawer).catch(() => {});
    });
    document.addEventListener('theme-drawer:open', (event) => {
      if (event.target?.id === 'cart-drawer') refreshCartDrawer();
    });
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) refreshCartDrawer();
    });

    const input = root.querySelector('[data-sky-header-search-input]');
    const results = root.querySelector('[data-sky-header-search-results]');
    const searchForm = root.querySelector('[data-sky-header-search-form]');
    let controller;
    const escapeHtml = (value) => {
      const element = document.createElement('div'); element.textContent = value || ''; return element.innerHTML;
    };
    const currencyPrice = (price) => {
      const amount = Number(price);
      if (!Number.isFinite(amount)) return '';
      const currency = window.Shopify?.currency?.active || 'USD';
      try { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(amount); }
      catch (error) { return `${amount.toFixed(2)} ${currency}`; }
    };
    const updateResults = async () => {
      const query = input?.value.trim();
      if (!query || query.length < 2) { if (results) results.hidden = true; return; }
      controller?.abort(); controller = new AbortController();
      try {
        const route = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${route}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=hide`, { signal: controller.signal });
        if (!response.ok) throw new Error('Search unavailable');
        const data = await response.json();
        const products = data.resources?.results?.products || [];
        results.innerHTML = products.map((product) => `<a href="${escapeHtml(product.url)}"><strong>${escapeHtml(product.title)}</strong><small>${product.price_min != null ? `From ${escapeHtml(currencyPrice(product.price_min))}` : 'View plans'}</small></a>`).join('');
        results.hidden = products.length === 0;
      } catch (error) { if (error.name !== 'AbortError' && results) results.hidden = true; }
    };
    input?.addEventListener('input', updateResults);
    searchForm?.addEventListener('submit', (event) => {
      const first = results?.querySelector('a');
      if (first && !results.hidden) { event.preventDefault(); window.location.href = first.href; }
    });
  };

  const initializeAll = (scope = document) => scope.querySelectorAll('[data-sky-header]').forEach(initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeAll());
  else initializeAll();
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
